/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { Props } from '.';

type HelpersProps = {
  layout: 'icon' | 'textIconLeading' | 'textIconTrailing' | 'text',
};

export function getButtonClasses(
  layout: HelpersProps['layout'],
  variant: Props['variant'],
  size: Props['size'],
) {
  const commonClasses = classNames(
    'group/button',
    'bg-radient-parent',
    'inline-flex items-center',
    'relative',
    'overflow-hidden',
    'cursor-pointer',
    'transition-all duration-300',
    'shrink-0',
    'hover:shadow-none',
  );

  const sizeClasses = classNames(
    layout === 'icon' ? [{
      'size-8': size === 'xsmall' || size === 'small',
      'size-9': size === 'medium',
      'size-10': size === 'large',
    }] : [
      variant === 'link' ? [
        'size-auto', 'px-0'
      ] : [
        (size === 'xsmall' || size === 'small') && [
          'px-2',
          {
            'text-button-sm': variant !== 'tertiary',
            'text-sm': variant === 'tertiary',
            'h-7': size === 'xsmall',
            'h-9': size === 'small',
            'pl-1': layout === 'textIconLeading',
            'pr-1': layout === 'textIconTrailing',
          },
        ],
        size === 'medium' && [
          'h-10',
          'px-3',
          {
            'text-button-sm': variant !== 'tertiary',
            'text-md': variant === 'tertiary',
            'pl-2': layout === 'textIconLeading',
            'pr-2': layout === 'textIconTrailing',
          },
        ],
        size === 'large' && [
          'text-button-lg',
          'h-11',
          'px-4',
          {
            'pl-3': layout === 'textIconLeading',
            'pr-3': layout === 'textIconTrailing',
          },
        ],
      ]
    ],
  );

  const variantClasses = getButtonVariantClasses(variant, layout);
  const disabledClasses = getButtonDisabledClasses(variant, layout);

  return [commonClasses, sizeClasses, variantClasses, disabledClasses];
};

function getButtonVariantClasses(
  variant: Props['variant'],
  layout: HelpersProps['layout'],
) {
  const variantClasses = classNames(
    variant === 'primary' && [
      'bg-follow-parent',
      'font-display font-medium uppercase',
      'text-button-primary',
      'bg-button-surface-primary hover:bg-button-surface-primary-hover',
      'rounded-button-sm',
      'border-(length:--outline-button)',
      {
        'hover:text-button-primary-hover': layout !== 'icon',
        'border-button-border-primary': layout !== 'icon',
        'shadow-(--shadow-button-primary)': layout !== 'icon',
        'hover:text-button-primary': layout === 'icon',
        'border-button-border-icon': layout === 'icon',
        'shadow-(--shadow-button-icon)': layout === 'icon',
      },
    ],
    variant === 'secondary' && [
      'bg-follow-parent',
      'font-display font-medium uppercase',
      'text-button-secondary hover:text-button-secondary-hover',
      'bg-button-surface-secondary hover:bg-button-surface-secondary-hover',
      'rounded-button-sm',
      'border-(length:--outline-button)',
      {
        'border-button-border-secondary': layout !== 'icon',
        'shadow-(--shadow-button-secondary)': layout !== 'icon',
        'border-transparent hover:border-button-border-secondary': layout === 'icon',
      },
    ],
    variant === 'tertiary' && [
      'bg-follow-parent',
      'font-display font-medium uppercase',
      'text-button-tertiary hover:text-button-tertiary-hover',
      'bg-button-surface-tertiary hover:bg-button-surface-tertiary-hover',
      'shadow-(--shadow-button-tertiary)',
      'rounded-button-sm',
      'border-button-border-tertiary border-(length:--outline-button)',
    ],
    variant === 'plain' && [
      'font-sans font-normal',
      'text-button-plain hover:text-button-plain-hover',
      'bg-button-surface-plain hover:bg-button-surface-plain-hover',
    ],
    variant === 'link' && [
      'font-sans font-normal',
      'text-button-tertiary',
      'border-b border-transparent',
      'hover:text-button-tertiary-hover',
      'hover:border-b-button-tertiary',
    ],
  );

  const focusClasses = classNames(
    'focus-visible:outline-dotted focus-visible:outline-white focus-visible:outline-2 focus-visible:outline-offset-3',
    'focus-visible:transition-none',
    'focus-visible:shadow-(--shadow-focus)',
    {
      'focus-visible:text-button-primary': variant === 'primary',
      'focus-visible:text-button-secondary': variant === 'secondary',
      'focus-visible:text-button-tertiary': variant === 'tertiary',
    }
  );

  return [variantClasses, focusClasses];
};

export function getButtonLayout(
  Icon: Props['icon'],
  children: Props['children'],
  iconPosition: Props['iconPosition'],
) {
  if (Icon && !children) {
    return 'icon';
  }
  if (Icon && children && iconPosition === 'before') {
    return 'textIconLeading';
  }
  if (Icon && children && iconPosition === 'after') {
    return 'textIconTrailing';
  }
  return 'text';
};

function getButtonDisabledClasses(
  variant: Props['variant'],
  layout: HelpersProps['layout'],
) {
  const disabledClasses = classNames(
    'disabled:opacity-30',
    'disabled:cursor-not-allowed',
    'disabled:data-loading:opacity-100',
    'disabled:data-loading:cursor-wait',
    'disabled:data-loading:*:opacity-50',
    variant === 'primary' && [
      'hover:disabled:text-button-primary',
      'hover:disabled:bg-button-surface-primary',
      {
        'hover:disabled:shadow-(--shadow-button-primary)': layout !== 'icon',
      }
    ],
    variant === 'secondary' && [
      'hover:disabled:text-button-secondary',
      'hover:disabled:bg-button-surface-secondary',
      {
        'hover:disabled:shadow-(--shadow-button-secondary)': layout !== 'icon',
      }
    ],
    variant === 'tertiary' && [
      'hover:disabled:text-button-tertiary',
      'hover:disabled:bg-button-surface-tertiary',
      {
        'hover:disabled:shadow-(--shadow-button-tertiary)': layout !== 'icon',
      }
    ],
    variant === 'plain' && [
      'hover:disabled:text-button-plain',
      'hover:disabled:bg-button-surface-plain',
    ],
    variant === 'link' && [
      'hover:disabled:text-button-tertiary',
      'hover:disabled:border-transparent',
    ],
  );
  return disabledClasses;
};

export function getIconClasses(
  children: Props['children'],
  size: Props['size'],
  iconPosition: Props['iconPosition'],
) {
  const iconClasses = classNames(
    'shrink',
    'inline-flex justify-center items-center',
    {
      'mr-2': children && iconPosition === 'before',
      'ml-2': children && iconPosition === 'after',
      'size-5': size !== 'large',
      'size-6': size === 'large',
    },
  );
  return iconClasses;
}